import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const CANONICAL = `${SITE_URL}/blog/life-insurance-self-employed-indiana`;
const OG_IMAGE = 'https://public.readdy.ai/ai/img_res/8f04845864ece5b2be1dbe642b6344a3.jpg';

export default function LifeInsuranceSelfEmployedIndiana() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set canonical URL
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = CANONICAL;

    document.title = 'Life Insurance for Self-Employed Indiana | 2025';

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Life Insurance for the Self-Employed in Indiana: Coverage, Costs & Tax Strategy (2025)',
        description: 'Why self-employed Hoosiers need more life insurance than W-2 employees, the best policy types for 1099 workers and business owners, and how to deduct premiums. Real 2025 Indiana rates.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-03-15T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: CANONICAL,
        mainEntityOfPage: 'https://hoosierlifeinsurance.com/blog/life-insurance-self-employed-indiana',
        image: { '@type': 'ImageObject', url: 'https://readdy.ai/api/search-image?query=self%20employed%20Indiana%20small%20business%20owner%20entrepreneur%20reviewing%20life%20insurance%20documents%20at%20modern%20home%20office%20desk%20with%20laptop%20coffee%20cup%20clean%20bright%20minimal%20background%20Indianapolis%20professional%20focused%20confident&width=1200&height=630&seq=blog-selfemployed-og&orientation=landscape', width: 1200, height: 630 },
        timeRequired: 'PT12M',
        wordCount: 2400,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Can self-employed people in Indiana deduct life insurance premiums?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Personal life insurance premiums (policies you own for personal benefit) are not tax-deductible for self-employed individuals. However, if you own a business and the policy is a key person policy or part of a buy-sell agreement with a business purpose, the structure may allow different treatment. Consult a CPA — the rules are nuanced.',
            },
          },
          {
            '@type': 'Question',
            name: 'How much life insurance does a self-employed person in Indiana need?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'More than a W-2 employee in most cases. Self-employed income is less predictable, you have no employer-paid group life, and your business may have debts that could affect your family. Add your personal DIME calculation (10-12x income) to any business liabilities, and consider a separate key person policy if your business revenue depends on you.',
            },
          },
          {
            '@type': 'Question',
            name: 'What type of life insurance is best for freelancers and 1099 contractors in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Term life is usually the best starting point for freelancers — maximum personal protection at the lowest cost. A 20-year term of $750K–$1.5M runs $35–$90/month depending on age and health. If you have a growing business, adding an IUL (indexed universal life) for tax-advantaged savings alongside a term policy is a common strategy used by Indianapolis business owners.',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-selfemployed-${idx}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => {
      schemas.forEach((_, idx) => document.getElementById(`schema-selfemployed-${idx}`)?.remove());
    };
  }, []);

  const coverageDifferences = [
    { aspect: 'Employer group life', employee: '1–2× salary (free)', selfEmployed: 'None — you pay 100%', risk: 'High' },
    { aspect: 'Disability income', employee: 'Often employer-provided', selfEmployed: 'None unless self-purchased', risk: 'High' },
    { aspect: 'Income variability', employee: 'Fixed salary', selfEmployed: 'Variable — coverage gap risk', risk: 'Medium' },
    { aspect: 'Business debt liability', employee: 'N/A', selfEmployed: 'May pass to estate/spouse', risk: 'High' },
    { aspect: 'Key person dependency', employee: 'N/A', selfEmployed: 'Business collapses without you', risk: 'High' },
    { aspect: 'Buy-sell need', employee: 'N/A', selfEmployed: 'Required if business partner', risk: 'Medium' },
  ];

  const rateTable = [
    { profile: 'Freelancer, 30, Male', income: '$65K', coverage: '$750K / 20yr', monthly: '$32' },
    { profile: 'Consultant, 35, Female', income: '$95K', coverage: '$1M / 20yr', monthly: '$38' },
    { profile: 'Contractor, 40, Male', income: '$80K', coverage: '$1M / 20yr', monthly: '$62' },
    { profile: 'Business Owner, 40, Female', income: '$120K', coverage: '$1.5M / 20yr', monthly: '$84' },
    { profile: 'Sole Proprietor, 45, Male', income: '$75K', coverage: '$750K / 20yr', monthly: '$88' },
    { profile: 'Realtor, 50, Female', income: '$100K', coverage: '$1M / 20yr', monthly: '$145' },
  ];

  const policyTypes = [
    {
      icon: 'ri-time-line',
      color: 'bg-[#1A3A52]',
      name: 'Term Life',
      best: 'Personal income replacement',
      pros: ['Cheapest per dollar of coverage', 'Simple — pure protection', '20–30yr term covers working years'],
      cons: ['No cash value', 'Expires if you outlive the term'],
      monthly: '$32–$145/mo',
    },
    {
      icon: 'ri-line-chart-line',
      color: 'bg-[#D97642]',
      name: 'Indexed Universal Life (IUL)',
      best: 'Tax-advantaged business owner savings',
      pros: ['Tax-deferred cash value growth', 'Tax-free loans for retirement', 'Flexible premiums match variable income'],
      cons: ['More complex', 'Higher cost than term'],
      monthly: '$200–$600/mo',
    },
    {
      icon: 'ri-briefcase-line',
      color: 'bg-emerald-600',
      name: 'Key Person Insurance',
      best: 'Protecting the business itself',
      pros: ['Business pays premium', 'Business receives death benefit', 'Keeps company afloat during transition'],
      cons: ['Doesn\'t directly protect family', 'Requires business ownership'],
      monthly: '$50–$300/mo',
    },
    {
      icon: 'ri-shake-hands-line',
      color: 'bg-amber-600',
      name: 'Buy-Sell Agreement Policy',
      best: 'Business owners with partners',
      pros: ['Funds partner buyout at death', 'Prevents family/partner conflict', 'Keeps business operating'],
      cons: ['Only relevant with partners', 'Requires legal agreement'],
      monthly: '$40–$200/mo',
    },
  ];

  return (
    <>
      <title>Life Insurance for Self-Employed Indiana | 2026 Complete Guide | Hoosier Life</title>
      <meta name="description" content="Self-employed in Indiana? Learn why 1099 workers need more coverage than W-2 employees, the best policy types for freelancers & contractors, and real 2026 rates from top carriers." />
      <meta name="keywords" content="life insurance self employed Indiana, life insurance freelancer Indiana, 1099 contractor life insurance Indiana, business owner life insurance Indianapolis" />
      <meta property="og:title" content="Life Insurance for Self-Employed Indiana: Complete 2025 Guide" />
      <meta property="og:description" content="No employer group life? Here's the complete guide to life insurance for Indiana freelancers, contractors, and small business owners — costs, coverage, and tax strategy." />
      <meta property="og:url" content={CANONICAL} />
      <link rel="canonical" href={CANONICAL} />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={OG_IMAGE} />

      <BlogProgressBar />
      <Header />
      <article className="min-h-screen bg-white">

        {/* Hero */}
        <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-[#D97642] mb-6 transition-colors text-sm cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#D97642]/20 text-[#D97642] text-xs font-bold px-3 py-1.5 rounded-full">Self-Employed</span>
              <span className="text-white/50 text-xs">12 min read · March 2025</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Life Insurance for the Self-Employed in Indiana: 2026 Complete Guide</h1>
            <p className="text-white/75 text-xl leading-relaxed max-w-2xl">No group plan. No HR department. No safety net — unless you build it yourself. Here's exactly what Indiana freelancers, contractors, and business owners need to know.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Get My Quote</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-semibold border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer">Free Strategy Call</Link>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">

          {/* Alert box */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl mb-10">
            <p className="font-bold text-amber-800 mb-2 flex items-center gap-2"><i className="ri-alert-line text-amber-500 text-lg"></i>The Self-Employed Coverage Gap</p>
            <p className="text-amber-700 leading-relaxed text-[15px]">
              The average W-2 employee in Indiana has <strong>$180,000+ in employer-sponsored life insurance</strong> they've never paid a premium for. Self-employed Hoosiers start from zero. Indiana has over 340,000 self-employed workers — and most are dramatically underinsured.
            </p>
          </div>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Indiana has 340,000+ self-employed workers — most are dramatically underinsured because they have zero employer-provided life insurance unlike W-2 employees',
                'Self-employed Hoosiers should multiply income by 15 (not 10) when calculating coverage — variable income means the surviving family needs more financial runway',
                'A freelancer earning $80,000/year in Indiana likely needs $1.5M+ in life insurance — personal income replacement + business liabilities + personal debts + education costs',
                'IUL (Indexed Universal Life) is the most popular supplemental strategy for high-income self-employed Indianapolis residents — tax-deferred growth with flexible premiums matching variable income',
                'Personal life insurance premiums are NOT tax-deductible for self-employed individuals — however, IUL cash value grows tax-deferred and loans are tax-free',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Why you need more */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Why the Self-Employed Need More Coverage — Not Less</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A W-2 employee in Indiana averages $180,000+ in employer-provided life insurance at zero personal cost. A self-employed Hoosier starts with zero. Beyond that gap, self-employed individuals face additional risks: variable income that's hard to replace, business liabilities that can pass to the estate, and no employer retirement match — making comprehensive personal coverage essential.
            </div>
            <p className="text-gray-600 mb-6">Being your own boss comes with more financial exposure than most freelancers realize. Here's the full risk landscape compared to a traditional employee:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Risk Factor', 'W-2 Employee', 'Self-Employed / 1099', 'Risk Level'].map(h => (
                      <th key={h} className="px-5 py-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {coverageDifferences.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium text-gray-900">{r.aspect}</td>
                      <td className="px-5 py-3 text-emerald-700">{r.employee}</td>
                      <td className="px-5 py-3 text-red-600 font-medium">{r.selfEmployed}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.risk === 'High' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.risk}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Coverage calculation */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How to Calculate Your Coverage Need as a Self-Employed Hoosier</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Use a 4-part formula: (1) Annual net income × 15 years, (2) + personal debts and mortgage, (3) + business liabilities, (4) + education costs per child. For an Indiana sole proprietor earning $80K/year with a family and business debt, total recommended coverage typically lands at $1.5M–$2M — significantly more than a W-2 employee at the same income level.
            </div>
            <div className="space-y-4">
              {[
                { step: 'Personal Income Replacement', formula: 'Annual net income × 15 years', note: 'Use 15 years instead of 10 — variable income means your family needs more buffer time to stabilize.', example: '$80,000 × 15 = $1,200,000', color: 'border-[#1A3A52]', bg: 'bg-[#1A3A52]/5' },
                { step: 'Personal Debts & Mortgage', formula: 'Mortgage + car loans + credit cards + student loans', note: 'These pass to your estate regardless of employment status.', example: '+$215,000', color: 'border-[#D97642]', bg: 'bg-[#D97642]/5' },
                { step: 'Business Liabilities', formula: 'Business loans + lines of credit + accounts payable', note: 'As a sole proprietor, business debt is personal debt. An LLC provides some protection, but personal guarantees are common.', example: '+$85,000', color: 'border-amber-400', bg: 'bg-amber-50' },
                { step: 'Education Costs (per child)', formula: 'Purdue: ~$100K | IU: ~$105K | Private: ~$220K', note: 'Same as any other Indiana family — college doesn\'t cost less because you\'re self-employed.', example: '+$200,000 (2 kids)', color: 'border-emerald-500', bg: 'bg-emerald-50' },
              ].map((s, i) => (
                <div key={i} className={`border-l-4 ${s.color} ${s.bg} p-5 rounded-r-xl`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-1">{s.step}</h3>
                      <p className="text-gray-600 text-sm font-mono mb-1">{s.formula}</p>
                      <p className="text-gray-500 text-xs">{s.note}</p>
                    </div>
                    <span className="font-black text-gray-800 text-lg whitespace-nowrap">{s.example}</span>
                  </div>
                </div>
              ))}
              <div className="bg-[#D97642] rounded-xl p-5 flex items-center justify-between text-white">
                <div>
                  <p className="font-bold text-lg">Total Recommended Coverage</p>
                  <p className="text-white/75 text-sm">Example: Indiana sole proprietor, $80K income, 2 kids, personal + business debts</p>
                </div>
                <span className="font-black text-2xl whitespace-nowrap">$1,700,000</span>
              </div>
            </div>
          </section>

          {/* Policy types */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">The 4 Policy Types Every Self-Employed Hoosier Should Know</h2>
            <p className="text-gray-600 mb-6">Different situations call for different solutions. Most Indiana business owners end up with 2 of these 4 working together:</p>
            <div className="grid md:grid-cols-2 gap-6">
              {policyTypes.map((p, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 hover:border-[#D97642]/30 transition-colors">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 ${p.color} rounded-xl flex items-center justify-center`}>
                      <i className={`${p.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{p.name}</h3>
                      <p className="text-xs text-[#D97642] font-medium">{p.best}</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Pros</p>
                    <ul className="space-y-1.5">
                      {p.pros.map((pro, j) => <li key={j} className="text-xs text-gray-700 flex items-start gap-2"><i className="ri-check-line text-emerald-500 flex-shrink-0 mt-0.5"></i>{pro}</li>)}
                    </ul>
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Cons</p>
                    <ul className="space-y-1.5">
                      {p.cons.map((con, j) => <li key={j} className="text-xs text-gray-700 flex items-start gap-2"><i className="ri-subtract-line text-amber-500 flex-shrink-0 mt-0.5"></i>{con}</li>)}
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-4 py-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">Typical monthly cost</span>
                    <span className="font-bold text-[#D97642] text-sm">{p.monthly}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Rate table */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Real 2025 Rate Samples for Indiana Self-Employed Workers</h2>
            <p className="text-gray-600 mb-6">Term life rates for typical self-employed profiles across Indiana. All rates are for healthy, non-smoking applicants on 20-year term policies:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Profile', 'Annual Income', 'Coverage', 'Est. Monthly'].map(h => (
                      <th key={h} className="px-5 py-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rateTable.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium text-gray-900">{r.profile}</td>
                      <td className="px-5 py-3 text-gray-600">{r.income}</td>
                      <td className="px-5 py-3 text-gray-800 font-semibold">{r.coverage}</td>
                      <td className="px-5 py-3 text-[#D97642] font-bold text-lg">{r.monthly}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">*Estimated 2025 rates, preferred health class. Actual rates depend on full application.</p>
          </section>

          {/* Tax deductibility */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Life Insurance & Taxes for Indiana Self-Employed Workers</h2>
            <p className="text-gray-600 mb-5">The tax rules for self-employed life insurance are nuanced. Here's what you need to know before making decisions:</p>
            <div className="space-y-4">
              {[
                { title: 'Personal term life premiums: NOT deductible', icon: 'ri-close-circle-line', color: 'text-red-500', body: 'Premiums you pay for your own personal life insurance are not deductible on your Schedule C or personal return, whether you\'re a sole proprietor, LLC, or S-Corp owner. The IRS views the benefit as personal, not business.' },
                { title: 'Key person premiums: Generally NOT deductible', icon: 'ri-close-circle-line', color: 'text-red-500', body: 'Even business-owned key person policies are typically not deductible. However, the death benefit IS received tax-free by the business under IRC Section 101(a). Consult your CPA.' },
                { title: 'IUL cash value growth: Tax-deferred', icon: 'ri-check-circle-line', color: 'text-emerald-500', body: 'The cash value inside an IUL policy grows tax-deferred. Loans taken against cash value are generally tax-free. This is a major tax advantage for high-income self-employed Hoosiers.' },
                { title: 'Buy-sell agreement: Premium treatment varies', icon: 'ri-information-line', color: 'text-amber-500', body: 'Buy-sell life insurance premiums are generally not deductible. But the business context affects the structure significantly. Always involve a CPA and attorney when setting up a buy-sell agreement.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className={`${item.icon} ${item.color} text-lg flex-shrink-0`}></i>
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed pl-7">{item.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 my-8 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">Our licensed Indiana agents specialize in life insurance for self-employed Hoosiers, freelancers, contractors, and business owners. We understand variable income underwriting and design coverage strategies that protect both the individual and their business.</p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: 'How much life insurance does a self-employed person in Indiana need?', a: 'More than a W-2 employee in most cases. Self-employed income is less predictable, you have no employer group life, and your business may have debts that affect your family. Multiply your net annual income by 15 (not 10) to account for income variability, then add business liabilities, personal debts, and education costs.' },
                { q: 'What type of life insurance is best for freelancers in Indiana?', a: 'Term life is the best starting point for most freelancers — maximum personal protection at the lowest cost. A 20-year $1M term policy for a healthy 35-year-old runs about $35–45/month. If you\'re profitable and thinking about tax-advantaged retirement savings, layer an IUL on top after your term is in place.' },
                { q: 'Can self-employed people get life insurance without a W-2?', a: 'Absolutely. Carriers underwrite based on your total income including 1099 income, Schedule C profit, K-1 distributions, and other sources. You\'ll need 1–2 years of tax returns showing income. Sole proprietors, LLC owners, S-Corp shareholders, and independent contractors all qualify.' },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-[#D97642] mt-0.5 flex-shrink-0"></i>{item.q}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed pl-6">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center text-white mb-12">
            <h2 className="text-2xl font-bold mb-3">Build Your Self-Employed Safety Net Today</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">We specialize in helping Indiana freelancers, contractors, and business owners get the right coverage — personal and business. Free 30-minute strategy call with no obligation.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/wealth-strategy-call" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Book Free Strategy Call</Link>
              <Link to="/instant-quote" className="px-6 py-3 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap cursor-pointer">Get Instant Quote</Link>
            </div>
          </section>

        </div>
      </article>

      <BlogRelatedStrip currentSlug="life-insurance-self-employed-indiana" />
      <Footer />
    </>
  );
}
